import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Cursor } from "./components/Cursor";
import { ScrollProgress } from "./components/ScrollProgress";
import { MobileCTA } from "./components/MobileCTA";
import { PageFade } from "./components/AnimatedSection";
import { Logo } from "./components/Logo";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Project from "./pages/Project";
import CaseStudy from "./pages/CaseStudy";
import Career from "./pages/Career";
import Contact from "./pages/Contact";
import WhatsAppButton from "./components/WhatsAppButton";

// Smooth momentum scrolling (skipped when the user prefers reduced motion).
const useLenis = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
};

// Scroll to top on route change; honor /services#anchor links.
const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const lenis = window.__lenis;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        const t = setTimeout(
          () => (lenis ? lenis.scrollTo(el, { offset: -110 }) : el.scrollIntoView({ behavior: "smooth" })),
          420,
        );
        return () => clearTimeout(t);
      }
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
};

// Simple gradient wipe on every navigation (~400ms sweep).
const Wipe = () => {
  const { pathname } = useLocation();
  return (
    <motion.div
      key={pathname}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[80] bg-gradient-to-r from-[#3CBE6E] via-[#1C7543] to-[#B8912A]"
      initial={{ x: "-101%" }}
      animate={{ x: "101%" }}
      transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1], delay: 0.05 }}
    />
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageFade><Home /></PageFade>} />
        <Route path="/services" element={<PageFade><Services /></PageFade>} />
        <Route path="/project" element={<PageFade><Project /></PageFade>} />
        <Route path="/project/:slug" element={<PageFade><CaseStudy /></PageFade>} />
        <Route path="/career" element={<PageFade><Career /></PageFade>} />
        <Route path="/contact" element={<PageFade><Contact /></PageFade>} />
        <Route path="*" element={<PageFade><Home /></PageFade>} />
      </Routes>
  );
};

// Premium loader: logo shimmer + gradient bar, then reveal. Never > 1.4s.
const Loader = ({ done }) => (
  <AnimatePresence>
    {!done && (
      <motion.div
        data-testid="site-loader"
        className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-[#07130C]"
        exit={{ y: "-100%" }}
        transition={{ duration: 0.65, ease: [0.76, 0, 0.24, 1] }}
      >
        <Logo size={72} animated stacked />
        <div className="mt-8 h-[2px] w-40 overflow-hidden rounded-full bg-white/10">
          <motion.div
            className="h-full bg-gradient-to-r from-[#3CBE6E] via-[#1C7543] to-[#B8912A]"
            initial={{ x: "-100%" }}
            animate={{ x: "0%" }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

function App() {
  const [loaded, setLoaded] = useState(false);
  useLenis();
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 1350);
    return () => clearTimeout(t);
  }, []);

  return (
    <BrowserRouter>
      {/* <Loader done={loaded} /> */}
      <ScrollManager />
      <Wipe />
      <Cursor />
      <ScrollProgress />
      <Header />
      <main>
        <AnimatedRoutes />
      </main>
      <Footer />
      <MobileCTA />
      <Toaster position="top-center" />
      <WhatsAppButton />
    </BrowserRouter>
  );
}

export default App;
